import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ActionBar, ActionButton, Box, Button, ErrorBoundary, H1, H2, Icon, IconSpritesheet, NumberStepper, Spinner, Tabs, Text, } from '@a-type/ui';
import { useLocalStorage, withGame, } from '@long-game/game-client';
import { Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlayerAvatar } from '../players/PlayerAvatar.js';
import { PlayerName } from '../players/PlayerName.js';
import cls from './GameDebugger.module.css';
export function GameDebugger({ ...props }) {
    const [popout, setPopout] = useState(null);
    const [root, setRoot] = useState(null);
    const [showButton] = useLocalStorage('debugEnabled', window.location.hostname === 'localhost');
    const showDebugger = () => {
        const newWindow = window.open('', 'Game Debugger', 'width=800,height=600,scrollbars=yes,resizable=yes');
        if (newWindow) {
            const root = newWindow.document.createElement('div');
            setRoot(root);
            newWindow.document.body.appendChild(root);
            // Copy the app's styles into the new window
            const stylesheets = Array.from(document.styleSheets);
            stylesheets.forEach((stylesheet) => {
                const css = stylesheet;
                if (stylesheet.href) {
                    const newStyleElement = document.createElement('link');
                    newStyleElement.rel = 'stylesheet';
                    newStyleElement.href = stylesheet.href;
                    newWindow.document.head.appendChild(newStyleElement);
                }
                else if (css && css.cssRules && css.cssRules.length > 0) {
                    const newStyleElement = document.createElement('style');
                    Array.from(css.cssRules).forEach((rule) => {
                        newStyleElement.appendChild(document.createTextNode(rule.cssText));
                    });
                    newWindow.document.head.appendChild(newStyleElement);
                }
            });
            newWindow.focus();
            setPopout(newWindow);
        }
    };
    useEffect(() => {
        const remove = () => {
            setRoot(null);
            setPopout(null);
        };
        popout?.addEventListener('beforeunload', remove);
        return () => {
            if (popout) {
                popout.removeEventListener('beforeunload', remove);
                popout.close();
            }
        };
    }, [popout]);
    if (!showButton)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(Button, { "aria-label": "Show debugger", onClick: showDebugger, ...props, children: _jsx(Icon, { name: "bug" }) }), _jsx(Suspense, { children: root && popout && createPortal(_jsx(DebuggerUi, {}), root) })] }));
}
const DebuggerUi = withGame(function DebuggerUi({ gameSuite }) {
    return (_jsxs(Box, { col: true, p: true, gap: true, children: [_jsx(IconSpritesheet, {}), _jsx(H1, { children: "Game Debugger" }), _jsx(ActionBar, { children: _jsxs(ActionButton, { onClick: () => gameSuite.resetGame(), children: [_jsx(Icon, { name: "warning" }), "Reset Game"] }) }), _jsx(ErrorBoundary, { fallback: ({ error }) => _jsx("div", { children: error?.message }), children: _jsx(DebuggerContent, {}) })] }));
});
const DebuggerContent = withGame(function DebuggerContent({ gameSuite }) {
    const [debug, setDebug] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        function refresh() {
            console.log('refreshing debug data');
            gameSuite
                .debug()
                .then(setDebug)
                .catch((err) => {
                console.error('Error fetching debug data:', err);
                setError(err);
            });
        }
        refresh();
        const unsubs = [
            gameSuite.subscribe('turnPrepared', refresh),
            gameSuite.subscribe('turnPlayed', refresh),
            gameSuite.subscribe('roundChanged', refresh),
        ];
        return () => {
            unsubs.forEach((unsub) => unsub());
        };
    }, [gameSuite]);
    const [roundIndex, setRoundIndex] = useState(gameSuite.latestRoundIndex);
    if (!debug) {
        if (error) {
            return _jsxs("div", { children: ["Error: ", error.message] });
        }
        return _jsx(Spinner, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(Box, { gap: true, wrap: true, items: "center", children: gameSuite.members.map((member) => (_jsx(Box, { col: true, items: "center", children: _jsxs(Suspense, { children: [_jsx(PlayerAvatar, { playerId: member.id, size: "60px" }), _jsx(PlayerName, { playerId: member.id }), _jsx("div", { children: member.id })] }) }, member.id))) }), _jsxs(Tabs, { defaultValue: "globalState", children: [_jsxs(Tabs.List, { children: [_jsx(Tabs.Trigger, { value: "globalState", children: "Global State" }), _jsx(Tabs.Trigger, { value: "playerState", children: "Player State" }), _jsx(Tabs.Trigger, { value: "currentTurn", children: "Current Turn" }), _jsx(Tabs.Trigger, { value: "rounds", children: "Round History" })] }), _jsx(Tabs.Content, { value: "globalState", children: _jsxs(Box, { col: true, children: [_jsx(H2, { children: "Global State" }), _jsx("pre", { className: cls.pre, children: JSON.stringify(debug.globalState, null, 2) })] }) }), _jsx(Tabs.Content, { value: "playerState", children: _jsxs(Box, { col: true, children: [_jsx(H2, { children: "Player State" }), _jsxs(Tabs, { defaultValue: "final", children: [_jsxs(Tabs.List, { children: [_jsx(Tabs.Trigger, { value: "initial", children: "Initial" }), _jsx(Tabs.Trigger, { value: "final", children: "Final" })] }), _jsx(Tabs.Content, { value: "initial", children: _jsx("pre", { className: cls.pre, children: JSON.stringify(debug.initialState, null, 2) }) }), _jsx(Tabs.Content, { value: "final", children: _jsx("pre", { className: cls.pre, children: JSON.stringify(debug.finalState, null, 2) }) })] })] }) }), _jsx(Tabs.Content, { value: "currentTurn", children: _jsxs(Box, { col: true, children: [_jsx(H2, { children: "Current Turn" }), gameSuite.turnError && (_jsxs(Box, { color: "attention", surface: true, col: true, p: "sm", children: [_jsx(H2, { children: "Turn Validation Error" }), _jsx("pre", { className: cls.pre, children: JSON.stringify(gameSuite.turnError, null, 2) })] })), _jsx("pre", { children: JSON.stringify(debug.currentTurn, null, 2) })] }) }), _jsx(Tabs.Content, { value: "rounds", children: _jsxs(Box, { col: true, children: [_jsx(H2, { children: "Round History" }), _jsx(NumberStepper, { value: roundIndex, onChange: setRoundIndex, min: 0, max: gameSuite.latestRoundIndex }), _jsx(Suspense, { children: _jsx(RoundDebug, { roundIndex: roundIndex }) })] }) })] })] }));
});
const RoundDebug = withGame(function RoundDebug({ roundIndex, gameSuite, }) {
    const round = gameSuite.getRound(roundIndex);
    return (_jsx(Text, { render: _jsx("pre", {}), emphasis: "ambient", children: JSON.stringify(round, null, 2) }));
});
//# sourceMappingURL=GameDebugger.js.map